import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import useCountUp from '../hooks/useCountUp';
import { localDateStr, contactAge } from '../utils/dateUtils';
import { contactMatchesQuery } from '../utils/contactSearch';
import { smsInfo } from '../utils/sms';
import { findDuplicates, normalize } from '../utils/dataInsights';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { getAvatarStyle } from '../utils/avatar';
import { useApp } from '../context/AppContext';
import { Card, CardHeader } from '../components/UI/Card';
import Button from '../components/UI/Button';
import Badge, { ScoreBadge } from '../components/UI/Badge';
import { Input, Select } from '../components/UI/Input';
import { api } from '../services/api';
import { isVip } from '../utils/segments';
import './Contacts.css';

const PAGE_SIZE = 25;

const GENDER_BADGE = {
  monsieur:      { label: 'M.',  color: '#2563EB', bg: '#EFF6FF' },
  madame:        { label: 'Mme', color: '#DB2777', bg: '#FDF2F8' },
  autre:         { label: 'X',   color: '#7C3AED', bg: '#F5F3FF' },
  non_renseigne: { label: '?',   color: '#9CA3AF', bg: '#F9FAFB' },
};


function InlineMemory({ text, maxLength = 120 }) {
  const [expanded, setExpanded] = useState(false);
  if (!text) return null;
  const isLong = text.length > maxLength;
  return (
    <div className="ct-note-box">
      <span className="ct-note-icon">💡</span>
      <span className="ct-note-text">
        {!expanded && isLong ? text.slice(0, maxLength) + '…' : text}
        {isLong && (
          <button className="ct-note-btn" onClick={e => { e.stopPropagation(); setExpanded(v => !v); }}>
            {expanded ? ' Réduire' : ' Lire plus'}
          </button>
        )}
      </span>
    </div>
  );
}

/** Nombre de CONTACTS concernés par au moins un doublon (pas le nombre de paires). */
function countDuplicateContacts(pairs) {
  const ids = new Set();
  pairs.forEach(p => { ids.add(p.a.id); ids.add(p.b.id); });
  return ids.size;
}

function mergeContacts(a, b) {
  const best = (va, vb) => va || vb || null;
  return {
    ...a,
    first_name:          best(a.first_name, b.first_name),
    last_name:           best(a.last_name, b.last_name),
    email:               best(a.email, b.email),
    phone:               best(a.phone, b.phone),
    birth_date:          best(a.birth_date, b.birth_date),
    gender:              (a.gender && a.gender !== 'non_renseigne') ? a.gender : (b.gender || a.gender),
    address:             best(a.address, b.address),
    city:                best(a.city, b.city),
    postal_code:         best(a.postal_code, b.postal_code),
    region:              best(a.region, b.region),
    canton:              best(a.canton, b.canton),
    category:            a.category || b.category,
    tags:                [...new Set([...(a.tags || []), ...(b.tags || [])])],
    relationship_memory: [a.relationship_memory, b.relationship_memory].filter(Boolean).join(' | ') || null,
    quality_score:       Math.max(a.quality_score || 0, b.quality_score || 0),
    engagement_score:    Math.max(a.engagement_score || 0, b.engagement_score || 0),
    // Consentement : on préserve le plus favorable (une fusion de doublons = même personne)
    gdpr_consent:        !!(a.gdpr_consent || b.gdpr_consent),
    gdpr_consent_at:     best(a.gdpr_consent_at, b.gdpr_consent_at),
    sms_consent:         !!(a.sms_consent || b.sms_consent),
    status:              (a.status === 'active' || b.status === 'active') ? 'active' : a.status,
  };
}

/* ── DedupModal ── */
function DedupModal({ contacts, onClose, onApply }) {
  const pairs   = useMemo(() => findDuplicates(contacts), [contacts]);
  const [page, setPage] = useState(0);
  const [actions, setActions] = useState({});
  const [bulkAction, setBulkAction] = useState('');

  if (pairs.length === 0) {
    return (
      <div className="dedup-overlay" onClick={onClose}>
        <div className="dedup-modal" onClick={e => e.stopPropagation()}>
          <div className="dedup-header">
            <h3 className="dedup-title">Détection des doublons</h3>
            <button className="dedup-close" onClick={onClose}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M18 6 6 18M6 6l12 12"/></svg>
            </button>
          </div>
          <div className="dedup-empty">
            <div className="dedup-empty-icon">✅</div>
            <h4 className="dedup-empty-title">Aucun doublon détecté</h4>
            <p className="dedup-empty-sub">Votre base est propre — nom identique, même e-mail ou même téléphone+nom.</p>
            <Button onClick={onClose}>Fermer</Button>
          </div>
        </div>
      </div>
    );
  }

  const pair = pairs[page];
  const action = actions[pair.key] || null;
  const handled = Object.keys(actions).filter(k => actions[k] !== 'ignore').length;
  const setAction = (a) => setActions(prev => ({ ...prev, [pair.key]: a }));

  const handleBulkApply = () => {
    if (!bulkAction) return;
    const bulk = {};
    pairs.forEach(p => { bulk[p.key] = bulkAction; });
    setActions(bulk);
    setBulkAction('');
  };

  const handleApply = () => {
    const toMerge   = pairs.filter(p => actions[p.key] === 'merge');
    const toRemoveB = pairs.filter(p => actions[p.key] === 'keep_a');
    const toRemoveA = pairs.filter(p => actions[p.key] === 'keep_b');
    onApply({ toMerge, toRemoveA, toRemoveB });
    onClose();
  };

  const FIELD_LABELS = {
    email: 'E-mail', phone: 'Téléphone', city: 'Ville', birth_date: 'Date naissance',
    category: 'Catégorie', quality_score: 'Score',
  };

  return (
    <div className="dedup-overlay" onClick={onClose}>
      <div className="dedup-modal" onClick={e => e.stopPropagation()}>
        <div className="dedup-header">
          <div>
            <h3 className="dedup-title">
              <span className="dedup-title-icon">🔍</span>
              Doublons détectés — {pairs.length} paire{pairs.length > 1 ? 's' : ''}
            </h3>
            <p className="dedup-sub">Doublon {page + 1} / {pairs.length} · <strong>{handled}</strong> traité{handled !== 1 ? 's' : ''}</p>
          </div>
          <button className="dedup-close" onClick={onClose}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M18 6 6 18M6 6l12 12"/></svg>
          </button>
        </div>

        <div className="dedup-bulk-bar">
          <span className="dedup-bulk-label">Traitement en bloc :</span>
          <select className="dedup-bulk-select" value={bulkAction} onChange={e => setBulkAction(e.target.value)}>
            <option value="">— Choisir une action —</option>
            <option value="merge">🔀 Fusionner toutes les paires</option>
            <option value="keep_a">← Garder le premier de chaque paire</option>
            <option value="keep_b">Garder le second de chaque paire →</option>
            <option value="ignore">✓ Tout ignorer (pas des doublons)</option>
          </select>
          <button className="dedup-bulk-apply" disabled={!bulkAction} onClick={handleBulkApply}>
            Appliquer à tous ({pairs.length})
          </button>
        </div>

        <div className="dedup-progress">
          <div className="dedup-progress-fill" style={{ width: `${((page + 1) / pairs.length) * 100}%` }} />
        </div>

        <div className="dedup-reasons">
          {pair.reasons.map(r => <span key={r} className="dedup-reason-chip">{r}</span>)}
        </div>

        <div className="dedup-compare">
          {[pair.a, pair.b].map((c, idx) => (
            <div key={c.id} className={`dedup-contact ${action === (idx === 0 ? 'keep_a' : 'keep_b') ? 'dedup-contact-selected' : ''} ${action === (idx === 0 ? 'keep_b' : 'keep_a') ? 'dedup-contact-removed' : ''}`}>
              <div className="dedup-contact-header">
                <div className="dedup-avatar" style={getAvatarStyle(c.first_name)}>{(c.first_name || '?').charAt(0)}{(c.last_name || '').charAt(0)}</div>
                <div>
                  <div className="dedup-name">{c.first_name} {c.last_name}</div>
                  <div className="dedup-meta">ID #{c.id} · Score {c.quality_score}</div>
                </div>
              </div>
              <div className="dedup-fields">
                {Object.entries(FIELD_LABELS).map(([field, label]) => {
                  const val = c[field];
                  const other = (idx === 0 ? pair.b : pair.a)[field];
                  const differs = String(val||'') !== String(other||'');
                  return (
                    <div key={field} className={`dedup-field ${differs ? 'dedup-field-diff' : ''}`}>
                      <span className="dedup-field-label">{label}</span>
                      <span className="dedup-field-val">{val || <em style={{ color: '#9CA3AF' }}>—</em>}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        <div className="dedup-actions">
          <button className={`dedup-btn dedup-btn-merge ${action === 'merge' ? 'dedup-btn-active' : ''}`} onClick={() => setAction('merge')}>
            🔀 Fusionner (garder le meilleur)
          </button>
          <button className={`dedup-btn dedup-btn-keep ${action === 'keep_a' ? 'dedup-btn-active' : ''}`} onClick={() => setAction('keep_a')}>
            ← Garder {pair.a.first_name}
          </button>
          <button className={`dedup-btn dedup-btn-keep ${action === 'keep_b' ? 'dedup-btn-active' : ''}`} onClick={() => setAction('keep_b')}>
            Garder {pair.b.first_name} →
          </button>
          <button className={`dedup-btn dedup-btn-ignore ${action === 'ignore' ? 'dedup-btn-active' : ''}`} onClick={() => setAction('ignore')}>
            ✓ Pas des doublons
          </button>
        </div>

        {action === 'merge' && (
          <div className="dedup-merge-preview">
            <span className="dedup-merge-label">Résultat :</span>
            {' '}{pair.a.first_name} {pair.a.last_name} ·{' '}
            {mergeContacts(pair.a, pair.b).email || '—'} ·{' '}
            {mergeContacts(pair.a, pair.b).phone || '—'} ·{' '}
            {mergeContacts(pair.a, pair.b).city || '—'}
          </div>
        )}

        <div className="dedup-footer">
          <Button variant="ghost" disabled={page === 0} onClick={() => setPage(p => p - 1)}>← Précédent</Button>
          <div style={{ display: 'flex', gap: 8 }}>
            {handled > 0 && (
              <Button onClick={handleApply}>Appliquer {handled} action{handled > 1 ? 's' : ''}</Button>
            )}
            {page < pairs.length - 1 ? (
              <Button variant="secondary" onClick={() => setPage(p => p + 1)}>Suivant →</Button>
            ) : (
              handled > 0 && <Button onClick={handleApply}>✅ Terminer et appliquer</Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── StatCard ── */
function StatCard({ icon, label, value, sub, color, delay = 0 }) {
  const count = useCountUp(value, 1000);
  return (
    <div className="ct-stat-card" style={{ animationDelay: `${delay}ms` }}>
      <div className="ct-stat-icon" style={{ background: color }}>{icon}</div>
      <div className="ct-stat-body">
        <div className="ct-stat-value">{count.toLocaleString()}</div>
        <div className="ct-stat-label">{label}</div>
        {sub && <div className="ct-stat-sub">{sub}</div>}
      </div>
    </div>
  );
}

/* ── Main Contacts page ── */
export default function Contacts() {
  const { contacts, setContacts, addNotification } = useApp();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initNoEmail = searchParams.get('noEmail') === '1';
  const [search, setSearch]           = useState('');
  const [filterCity, setFilterCity]   = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterStatus, setFilterStatus]     = useState(initNoEmail ? 'active' : '');
  const [filterGender, setFilterGender]     = useState('');
  const [filterNoEmail, setFilterNoEmail]   = useState(initNoEmail);
  const [filterBdayMonth, setFilterBdayMonth] = useState(false);
  const [filterInactive, setFilterInactive]   = useState(false);
  const [page, setPage]               = useState(1);
  const [showDedup, setShowDedup]     = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkCategory, setBulkCategory] = useState('');
  const [smsTarget, setSmsTarget]     = useState(null);
  const [smsMsg, setSmsMsg]           = useState('');
  const [smsSending, setSmsSending]   = useState(false);
  const [consentSummary, setConsentSummary] = useState(null);
  const [consentFixing, setConsentFixing]   = useState(false);

  // Résumé consentement (bandeau nLPD) — silencieux si l'endpoint échoue
  useEffect(() => {
    api.getConsentSummary().then(setConsentSummary).catch(() => {});
  }, [contacts.length]);

  const handleConfirmConsent = async () => {
    if (!window.confirm(
      `Tu attestes que ces ${consentSummary.missing} contacts t'ont donné leur accord pour recevoir tes communications ?\n\n` +
      `Leur consentement sera enregistré et horodaté (les refus explicites ne sont jamais modifiés).`
    )) return;
    setConsentFixing(true);
    try {
      const res = await api.confirmConsentBulk();
      addNotification(`🛡 Consentement enregistré pour ${res.updated} contacts.`);
      setConsentSummary(s => ({ ...s, granted: s.granted + res.updated, missing: 0 }));
    } catch (err) {
      addNotification(err.message || 'Erreur lors de la régularisation', 'error');
    } finally {
      setConsentFixing(false);
    }
  };

  const toggleSelect = (id) => setSelectedIds(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const selectAll    = () => setSelectedIds(new Set(paginated.map(c => c.id)));
  const clearSelect  = () => setSelectedIds(new Set());

  const handleBulkDelete = async () => {
    if (!window.confirm(`Supprimer définitivement ${selectedIds.size} contact${selectedIds.size > 1 ? 's' : ''} ?`)) return;
    const ids = [...selectedIds];
    await Promise.allSettled(ids.map(id => api.deleteContact(id)));
    setContacts(cs => cs.filter(c => !selectedIds.has(c.id)));
    clearSelect();
    addNotification(`🗑 ${ids.length} contacts supprimés.`);
  };

  const handleBulkTag = async () => {
    if (!bulkCategory) { addNotification('Choisissez une catégorie.', 'error'); return; }
    const ids = [...selectedIds];
    await Promise.allSettled(ids.map(id => api.updateContact(id, { category: bulkCategory })));
    setContacts(cs => cs.map(c => selectedIds.has(c.id) ? { ...c, category: bulkCategory } : c));
    clearSelect();
    addNotification(`✅ ${ids.length} contacts classés "${bulkCategory}".`);
  };

  const handleBulkExport = () => {
    const sel = filtered.filter(c => selectedIds.has(c.id));
    const civLabel = g => ({ monsieur: 'Monsieur', madame: 'Madame', autre: 'Autre' }[g] || '');
    const rows = [['Civilité','Prénom','Nom','E-mail','Téléphone','Date naissance','Ville','Catégorie','Statut']];
    sel.forEach(c => rows.push([civLabel(c.gender), c.first_name||'', c.last_name||'', c.email||'', c.phone||'', c.birth_date||'', c.city||'', c.category||'', c.status||'']));
    const csv = rows.map(r => r.map(v => `"${String(v).replace(/"/g,'""')}"`).join(';')).join('\n');
    const a = document.createElement('a');
    a.href = 'data:text/csv;charset=utf-8,﻿' + encodeURIComponent(csv);
    a.download = `selection-${sel.length}-contacts.csv`; a.click();
    addNotification(`${sel.length} contacts exportés.`);
  };

  const handleBulkMerge = async () => {
    const sel = filtered.filter(c => selectedIds.has(c.id));
    if (sel.length !== 2) return;
    // On garde la fiche la mieux renseignée (e-mail, tél au format +41, ville).
    const score = (c) => (c.email ? 2 : 0) + (String(c.phone || '').startsWith('+') ? 1 : 0) + (c.city ? 1 : 0) + (c.birth_date ? 1 : 0);
    const [a, b] = score(sel[0]) >= score(sel[1]) ? [sel[0], sel[1]] : [sel[1], sel[0]];
    if (!window.confirm(`Fusionner « ${a.first_name} ${a.last_name} » en une seule fiche (on garde les meilleures infos des deux, l'autre est supprimée) ?`)) return;
    await handleDedupApply({ toMerge: [{ a, b }], toRemoveA: [], toRemoveB: [] });
    clearSelect();
  };

  const cities     = [...new Set(contacts.map(c => c.city).filter(Boolean))].sort();
  const categories = [...new Set(contacts.map(c => c.category).filter(Boolean))].sort();

  const thisMonth = new Date().getMonth() + 1;

  const filtered = useMemo(() => {
    let list = contacts;
    if (search) list = list.filter(c => contactMatchesQuery(c, search));
    if (filterCity)       list = list.filter(c => c.city === filterCity);
    if (filterCategory)   list = list.filter(c => c.category === filterCategory);
    if (filterStatus)     list = list.filter(c => c.status === filterStatus);
    if (filterGender)     list = list.filter(c => c.gender === filterGender);
    if (filterNoEmail)    list = list.filter(c => !c.email);
    if (filterBdayMonth)  list = list.filter(c => c.birth_date && parseInt(c.birth_date.slice(5, 7), 10) === thisMonth);
    if (filterInactive)   list = list.filter(c => c.status === 'inactive');
    return list;
  }, [contacts, search, filterCity, filterCategory, filterStatus, filterGender, filterNoEmail, filterBdayMonth, filterInactive, thisMonth]);

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const paginated  = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const handleExport = () => {
    try {
      const url = api.exportContacts();
      if (url && url.startsWith('http')) {
        const a = document.createElement('a');
        a.href = url; a.download = 'contacts-appee.csv'; a.click();
        addNotification(`Export en cours — ${contacts.length} contacts`);
        return;
      }
    } catch {}
    const civLabel = g => ({ monsieur: 'Monsieur', madame: 'Madame', autre: 'Autre' }[g] || '');
    const rows = [['Civilité','Prénom','Nom','E-mail','Téléphone','Date naissance','Ville','Catégorie','Statut','Mémoire']];
    filtered.forEach(c => rows.push([
      civLabel(c.gender), c.first_name||'', c.last_name||'', c.email||'',
      c.phone||'', c.birth_date||'', c.city||'', c.category||'', c.status||'',
      (c.relationship_memory||'').replace(/;/g, ','),
    ]));
    const csv = rows.map(r => r.map(v => `"${String(v).replace(/"/g,'""')}"`).join(';')).join('\n');
    const a = document.createElement('a');
    a.href = 'data:text/csv;charset=utf-8,﻿' + encodeURIComponent(csv);
    a.download = `contacts-appee-${localDateStr()}.csv`;
    a.click();
    addNotification(`${filtered.length} contacts exportés`);
  };

  const handleDedupApply = async ({ toMerge, toRemoveA, toRemoveB }) => {
    const mergedCount  = toMerge.length;
    const removedCount = toRemoveA.length + toRemoveB.length;

    // Champs éditables uniquement (le back filtre le reste via validate()).
    const editable = (m) => {
      const { id, tenant_id, created_at, updated_at, contacts_count, ...rest } = m; // eslint-disable-line no-unused-vars
      return rest;
    };

    let updated = [...contacts];
    const ops = [];

    // Fusions : le contact gardé (a) est mis à jour, le doublon (b) est supprimé — CÔTÉ SERVEUR.
    toMerge.forEach(({ a, b }) => {
      const merged = mergeContacts(a, b);
      updated = updated.map(c => c.id === a.id ? merged : c).filter(c => c.id !== b.id);
      ops.push(api.updateContact(a.id, editable(merged)).catch(() => null));
      ops.push(api.deleteContact(b.id).catch(() => null));
    });
    // Suppressions pures
    toRemoveB.forEach(({ b }) => { updated = updated.filter(c => c.id !== b.id); ops.push(api.deleteContact(b.id).catch(() => null)); });
    toRemoveA.forEach(({ a }) => { updated = updated.filter(c => c.id !== a.id); ops.push(api.deleteContact(a.id).catch(() => null)); });

    setShowDedup(false);
    setContacts(updated); // optimiste, confirmé par la persistance ci-dessous

    const results = await Promise.allSettled(ops);
    const failed = results.filter(r => r.status === 'fulfilled' && r.value === null).length;

    const msg = [
      mergedCount > 0 && `${mergedCount} fusion${mergedCount > 1 ? 's' : ''}`,
      removedCount > 0 && `${removedCount} suppression${removedCount > 1 ? 's' : ''}`,
    ].filter(Boolean).join(', ');

    if (failed > 0) {
      addNotification(`Doublons traités (${msg}) — ${failed} opération(s) n'ont pas pu être enregistrées, réessaie.`, 'error');
    } else {
      addNotification(`✅ Doublons traités et enregistrés : ${msg}`);
    }
  };

  const dupCount       = useMemo(() => countDuplicateContacts(findDuplicates(contacts)), [contacts]);
  const vipCount       = contacts.filter(isVip).length;
  const noEmail        = contacts.filter(c => !c.email).length;
  const inactiveCount  = contacts.filter(c => c.status === 'inactive').length;
  const activeCount    = contacts.filter(c => c.status === 'active').length;
  const withBday       = contacts.filter(c => !!c.birth_date).length;
  const bdayMonthCount = contacts.filter(c => c.birth_date && parseInt(c.birth_date.slice(5, 7), 10) === thisMonth).length;
  const baseScore      = contacts.length > 0
    ? Math.round(((contacts.length - noEmail) / contacts.length * 50) + (withBday / contacts.length * 50))
    : 0;

  const hasActiveFilters = search || filterCity || filterCategory || filterStatus || filterGender || filterNoEmail || filterBdayMonth || filterInactive;

  const resetFilters = () => {
    setSearch(''); setFilterCity(''); setFilterCategory('');
    setFilterStatus(''); setFilterGender(''); setFilterNoEmail(false);
    setFilterBdayMonth(false); setFilterInactive(false); setPage(1);
  };

  /* Pagination pages */
  const pageNums = useMemo(() => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const pages = new Set([1, totalPages, page, page - 1, page + 1].filter(p => p >= 1 && p <= totalPages));
    return [...pages].sort((a, b) => a - b);
  }, [totalPages, page]);

  return (
    <div className="ct-page">

      {/* ── Hero header ── */}
      <div className="ct-hero ct-animate-in">
        <div className="ct-hero-nebula" />
        <div className="ct-hero-top">
          <div className="ct-hero-left">
            <div className="ct-hero-icon-wrap">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,.95)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                <circle cx="9" cy="7" r="4"/>
                <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
              </svg>
            </div>
            <div>
              <h1 className="ct-hero-title">Contacts</h1>
              <p className="ct-hero-sub">
                {contacts.length.toLocaleString()} contact{contacts.length > 1 ? 's' : ''} dans la base
                {contacts.length > 0 && (
                  <span className="ct-base-score" style={{
                    marginLeft: 10,
                    background: baseScore >= 70 ? 'rgba(16,185,129,.25)' : baseScore >= 40 ? 'rgba(245,158,11,.25)' : 'rgba(239,68,68,.2)',
                    color: baseScore >= 70 ? '#065F46' : baseScore >= 40 ? '#92400E' : '#991B1B',
                    borderRadius: 20, padding: '2px 9px', fontSize: 12, fontWeight: 700,
                  }}>
                    Qualité {baseScore}%
                  </span>
                )}
              </p>
            </div>
          </div>
          <div className="ct-hero-actions">
            {dupCount > 0 && (
              <button className="ct-btn-warn" onClick={() => setShowDedup(true)}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
                Doublons ({dupCount})
              </button>
            )}
            <button className="ct-btn-ghost" onClick={handleExport}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
              Exporter
            </button>
            <Link to="/collecte"><button className="ct-btn-ghost">🧲 Collecter</button></Link>
            <Link to="/imports">
              <button className="ct-btn-ghost">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                Importer
              </button>
            </Link>
            <button className="ct-btn-primary" onClick={() => navigate('/contacts/new')}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
              Nouveau contact
            </button>
          </div>
        </div>

        {/* Stat cards */}
        <div className="ct-stats-row">
          <StatCard icon="👥" label="Total contacts" value={contacts.length} color="rgba(90,53,255,.25)" delay={0} />
          <StatCard icon="✅" label="Actifs" value={activeCount} sub={`${contacts.length > 0 ? Math.round(activeCount / contacts.length * 100) : 0}% de la base`} color="rgba(16,185,129,.25)" delay={60} />
          <StatCard icon="⭐" label="VIP" value={vipCount} color="rgba(245,158,11,.25)" delay={120} />
          <StatCard icon="🎂" label="Avec anniversaire" value={withBday} sub={`${contacts.length > 0 ? Math.round(withBday/contacts.length*100) : 0}% de la base`} color="rgba(251,191,36,.25)" delay={180} />
        </div>

        {/* ── Segments rapides ───────────────────────────────────────────── */}
        {contacts.length > 0 && (
          <div className="ct-quick-segs">
            {[
              { label: '⭐ VIP',             count: vipCount,       active: filterCategory === 'VIP',  onClick: () => { setFilterCategory(filterCategory === 'VIP' ? '' : 'VIP'); setPage(1); } },
              { label: '📧 Sans e-mail',     count: noEmail,        active: filterNoEmail,             onClick: () => { setFilterNoEmail(!filterNoEmail); setPage(1); } },
              { label: '🎂 Anniv. ce mois',  count: bdayMonthCount, active: filterBdayMonth,           onClick: () => { setFilterBdayMonth(!filterBdayMonth); setPage(1); } },
              { label: '💤 Inactifs',        count: inactiveCount,  active: filterInactive,            onClick: () => { setFilterInactive(!filterInactive); setPage(1); } },
            ].map(seg => (
              <button
                key={seg.label}
                className={`ct-quick-seg${seg.active ? ' ct-quick-seg-active' : ''}${seg.count === 0 ? ' ct-quick-seg-zero' : ''}`}
                onClick={seg.count > 0 ? seg.onClick : undefined}
              >
                {seg.label}
                <span className="ct-quick-seg-count">{seg.count}</span>
              </button>
            ))}
          </div>
        )}

        {/* ── Bandeau consentement (nLPD/RGPD) ─────────────────────────── */}
        {consentSummary && consentSummary.missing > 0 && (
          <div className="ct-consent-banner">
            <div className="ct-consent-banner-txt">
              <strong>🛡 {consentSummary.missing} contact{consentSummary.missing > 1 ? 's' : ''} sans consentement enregistré.</strong>{' '}
              La loi suisse (nLPD) demande de pouvoir prouver leur accord. Si ces contacts t'ont
              donné leur accord (clients, inscrits…), régularise en un clic — ce sera horodaté.
            </div>
            <button
              className="ct-consent-banner-btn"
              disabled={consentFixing}
              onClick={handleConfirmConsent}
            >
              {consentFixing ? '…' : `✅ J'atteste leur accord (${consentSummary.missing})`}
            </button>
          </div>
        )}

      </div>

      {/* 🔎 Recherche & filtres — SORTIS du hero pour l'alléger : le hero porte
          l'identité + les stats + les actions, la toolbar porte « filtrer la liste ». */}
      <div className="ct-toolbar ct-animate-in">
        {/* Filters */}
        <div className="ct-filters-bar">
          <div className="ct-filter-search-wrap">
            <svg className="ct-filter-search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
            <input
              className="ct-filter-search"
              placeholder="Rechercher nom, e-mail, ville…"
              value={search}
              onChange={e => { setSearch(e.target.value); setPage(1); }}
            />
          </div>
          <select className="ct-filter-select" value={filterCity} onChange={e => { setFilterCity(e.target.value); setPage(1); }}>
            <option value="">Toutes les villes</option>
            {cities.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <select className="ct-filter-select" value={filterCategory} onChange={e => { setFilterCategory(e.target.value); setPage(1); }}>
            <option value="">Toutes catégories</option>
            {categories.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <select className="ct-filter-select" value={filterStatus} onChange={e => { setFilterStatus(e.target.value); setPage(1); }}>
            <option value="">Tous statuts</option>
            <option value="active">Actif</option>
            <option value="inactive">Inactif</option>
            <option value="unsubscribed">Désinscrit</option>
          </select>
          <select className="ct-filter-select" value={filterGender} onChange={e => { setFilterGender(e.target.value); setPage(1); }}>
            <option value="">Toutes civilités</option>
            <option value="monsieur">M. (Monsieur)</option>
            <option value="madame">Mme (Madame)</option>
            <option value="autre">Autre / X</option>
            <option value="non_renseigne">Non renseigné</option>
          </select>
          {hasActiveFilters && (
            <button className="ct-filter-reset" onClick={resetFilters}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M18 6 6 18M6 6l12 12"/></svg>
              Réinitialiser
            </button>
          )}
        </div>

        {/* Results + active filter chips */}
        <div className="ct-results-bar">
          <span className="ct-results-count">
            <span className="ct-results-num">{filtered.length.toLocaleString()}</span> résultat{filtered.length > 1 ? 's' : ''}
          </span>
          <div className="ct-active-chips">
            {filterNoEmail && (
              <button className="ct-chip" onClick={() => { setFilterNoEmail(false); setPage(1); }}>
                📧 Sans e-mail <span className="ct-chip-x">✕</span>
              </button>
            )}
            {filterCity && (
              <button className="ct-chip" onClick={() => { setFilterCity(''); setPage(1); }}>
                📍 {filterCity} <span className="ct-chip-x">✕</span>
              </button>
            )}
            {filterCategory && (
              <button className="ct-chip" onClick={() => { setFilterCategory(''); setPage(1); }}>
                🏷 {filterCategory} <span className="ct-chip-x">✕</span>
              </button>
            )}
            {filterStatus && (
              <button className="ct-chip" onClick={() => { setFilterStatus(''); setPage(1); }}>
                {filterStatus === 'active' ? '✅ Actif' : filterStatus === 'unsubscribed' ? '🚫 Désinscrit' : '⭕ Inactif'} <span className="ct-chip-x">✕</span>
              </button>
            )}
            {filterGender && (
              <button className="ct-chip" onClick={() => { setFilterGender(''); setPage(1); }}>
                👤 {filterGender} <span className="ct-chip-x">✕</span>
              </button>
            )}
            {dupCount > 0 && (
              <button className="ct-dup-chip" onClick={() => setShowDedup(true)}>
                ⚠️ {dupCount} doublon{dupCount > 1 ? 's' : ''} — Traiter
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ── Bulk action bar ── */}
      {selectedIds.size > 0 && (
        <div className="ct-bulk-bar">
          <div className="ct-bulk-check">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
          </div>
          <span className="ct-bulk-count">
            {selectedIds.size} contact{selectedIds.size > 1 ? 's' : ''} sélectionné{selectedIds.size > 1 ? 's' : ''}
          </span>
          <div className="ct-bulk-actions">
            <div className="ct-bulk-tag">
              <select
                value={bulkCategory}
                onChange={e => setBulkCategory(e.target.value)}
                className="ct-bulk-select"
              >
                <option value="">Classer dans…</option>
                {['VIP','Régulier','Occasionnel','Inactif','Newsletter','Prospect', ...categories]
                  .filter((v,i,a) => a.indexOf(v) === i)
                  .map(c => <option key={c} value={c}>{c}</option>)}
              </select>
              <button className="ct-bulk-btn" onClick={handleBulkTag} disabled={!bulkCategory}>Appliquer</button>
            </div>
            {selectedIds.size === 2 && (
              <button className="ct-bulk-btn" onClick={handleBulkMerge} title="Fusionner les 2 fiches en une seule (doublon)">
                🔀 Fusionner
              </button>
            )}
            <button className="ct-bulk-btn ct-bulk-btn-export" onClick={handleBulkExport}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
              Exporter
            </button>
            <button className="ct-bulk-btn ct-bulk-btn-danger" onClick={handleBulkDelete}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/></svg>
              Supprimer
            </button>
          </div>
          <button className="ct-bulk-close" onClick={clearSelect}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M18 6 6 18M6 6l12 12"/></svg>
          </button>
        </div>
      )}

      {/* ── Table ── */}
      <div className="ct-card ct-animate-in" style={{ animationDelay: '100ms' }}>
        <div className="ct-table-wrap">
          <table className="ct-table">
            <thead>
              <tr>
                <th style={{ width: 40, paddingLeft: 16 }}>
                  <input
                    type="checkbox"
                    className="ct-checkbox"
                    checked={paginated.length > 0 && paginated.every(c => selectedIds.has(c.id))}
                    onChange={e => e.target.checked ? selectAll() : clearSelect()}
                    title="Tout sélectionner"
                  />
                </th>
                <th>Contact</th>
                <th>E-mail</th>
                <th>Téléphone</th>
                <th>Ville</th>
                <th>Catégorie</th>
                <th>Qualité</th>
                <th>Statut</th>
                <th style={{ width: 40 }}></th>
              </tr>
            </thead>
            <tbody>
              {paginated.length === 0 && (
                <tr>
                  <td colSpan={9}>
                    <div className="ct-empty">
                      <div className="ct-empty-icon">{contacts.length === 0 ? '👥' : '🔍'}</div>
                      <div className="ct-empty-title">
                        {contacts.length === 0 ? 'Aucun contact dans la base' : 'Aucun résultat'}
                      </div>
                      <div className="ct-empty-sub">
                        {contacts.length === 0
                          ? 'Importez vos clients pour commencer.'
                          : 'Aucun contact ne correspond aux filtres sélectionnés.'}
                      </div>
                      {contacts.length === 0 && (
                        <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
                          <Link to="/imports"><button className="ct-empty-btn">📥 Importer des contacts</button></Link>
                          <Link to="/collecte"><button className="ct-empty-btn" style={{ background: 'var(--violet)', color: '#fff' }}>🧲 Créer ma page de collecte</button></Link>
                        </div>
                      )}
                      {contacts.length > 0 && hasActiveFilters && (
                        <button className="ct-empty-btn" onClick={resetFilters}>Effacer les filtres</button>
                      )}
                    </div>
                  </td>
                </tr>
              )}
              {paginated.map((c, idx) => {
                const isSelected = selectedIds.has(c.id);
                const gBadge = GENDER_BADGE[c.gender];
                const age = contactAge(c.birth_date);
                let bdayDays = null;
                if (c.birth_date) {
                  const [, bm, bd] = c.birth_date.split('-').map(Number);
                  const now = new Date();
                  const today0 = new Date(now.getFullYear(), now.getMonth(), now.getDate());
                  let next = new Date(now.getFullYear(), bm - 1, bd);
                  if (next < today0) next = new Date(now.getFullYear() + 1, bm - 1, bd);
                  const diff = Math.round((next - today0) / 86400000);
                  if (diff <= 14) bdayDays = diff;
                }
                return (
                  <tr
                    key={c.id}
                    className={`ct-row ${isSelected ? 'ct-row-selected' : ''}`}
                    style={{ animationDelay: `${idx * 25}ms` }}
                    onClick={() => navigate(`/contacts/${c.id}`)}
                  >
                    <td style={{ paddingLeft: 16 }} onClick={e => { e.stopPropagation(); toggleSelect(c.id); }}>
                      <input
                        type="checkbox"
                        className="ct-checkbox"
                        checked={isSelected}
                        onChange={() => toggleSelect(c.id)}
                        onClick={e => e.stopPropagation()}
                      />
                    </td>
                    <td>
                      <div className="ct-contact-cell">
                        {(() => {
                          // Âge rond → rosace dorée radar + badge âge (repérable en un scan)
                          const _a = contactAge(c.birth_date);
                          const _r = _a && _a % 10 === 0;
                          return (
                            <div className={`db2-avatar-wrap${_r ? ' db2-avatar-wrap--round' : ''}`}>
                              <div className="ct-avatar" style={getAvatarStyle(c.first_name)}>
                                {(c.first_name || '?').charAt(0)}{(c.last_name || '').charAt(0)}
                              </div>
                              {_r && <span className="db2-avatar-age">{_a}</span>}
                            </div>
                          );
                        })()}
                        <div className="ct-contact-info">
                          <div className="ct-contact-name">
                            {gBadge && (
                              <span className="ct-gender-chip" style={{ color: gBadge.color, background: gBadge.bg, borderColor: gBadge.color + '33' }}>
                                {gBadge.label}
                              </span>
                            )}
                            {c.first_name} {c.last_name}
                            {age !== null && <span className="ct-age">{age} ans</span>}
                            {bdayDays !== null && (
                              <span className={`ct-bday-soon${bdayDays === 0 ? ' ct-bday-today' : ''}`}>
                                {bdayDays === 0 ? '🎂 Aujourd\'hui !' : `🎂 dans ${bdayDays}j`}
                              </span>
                            )}
                          </div>
                          <InlineMemory text={c.relationship_memory} />
                        </div>
                      </div>
                    </td>
                    <td className="ct-cell-muted">{c.email || <span className="ct-dash">—</span>}</td>
                    <td className="ct-cell-muted">
                      {c.phone
                        ? <button className="ct-phone-btn" onClick={e => { e.stopPropagation(); setSmsTarget(c); setSmsMsg(''); }}>
                            {c.phone}<span className="ct-sms-pill">📱</span>
                          </button>
                        : <span className="ct-dash">—</span>}
                    </td>
                    <td className="ct-cell-muted">{c.city || <span className="ct-dash">—</span>}</td>
                    <td>{c.category && <span className="ct-cat-badge">{c.category}</span>}</td>
                    <td><ScoreBadge score={c.quality_score} size="sm" /></td>
                    <td>
                      <span className={`ct-status-chip ${c.status === 'active' ? 'ct-status-active' : 'ct-status-inactive'}`}>
                        <span className="ct-status-dot" />
                        {c.status === 'active' ? 'Actif' : c.status === 'unsubscribed' ? 'Désinscrit' : 'Inactif'}
                      </span>
                    </td>
                    <td onClick={e => e.stopPropagation()}>
                      <button className="ct-row-arrow" onClick={() => navigate(`/contacts/${c.id}`)}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M9 18l6-6-6-6"/></svg>
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* ── Pagination ── */}
        {totalPages > 1 && (
          <div className="ct-pagination">
            <button className="ct-page-btn ct-page-nav" disabled={page === 1} onClick={() => setPage(p => p - 1)}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M15 18l-6-6 6-6"/></svg>
            </button>
            <div className="ct-page-nums">
              {pageNums.map((n, i) => {
                const prev = pageNums[i - 1];
                return (
                  <React.Fragment key={n}>
                    {prev && n - prev > 1 && <span className="ct-page-ellipsis">…</span>}
                    <button
                      className={`ct-page-btn ${n === page ? 'ct-page-active' : ''}`}
                      onClick={() => setPage(n)}
                    >{n}</button>
                  </React.Fragment>
                );
              })}
            </div>
            <button className="ct-page-btn ct-page-nav" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M9 18l6-6-6-6"/></svg>
            </button>
            <span className="ct-page-info">{filtered.length.toLocaleString()} contact{filtered.length > 1 ? 's' : ''}</span>
          </div>
        )}
      </div>

      {/* ── Mini modal SMS rapide ── */}
      {smsTarget && (
        <div className="ct-sms-overlay" onClick={() => setSmsTarget(null)}>
          <div className="ct-sms-modal" onClick={e => e.stopPropagation()}>
            <button className="ct-sms-close" onClick={() => setSmsTarget(null)}>✕</button>
            <div className="ct-sms-header">
              <span style={{ fontSize: 22 }}>📱</span>
              <div>
                <div className="ct-sms-name">SMS à <strong>{smsTarget.first_name} {smsTarget.last_name}</strong></div>
                <div className="ct-sms-phone">{smsTarget.phone}</div>
              </div>
            </div>
            <textarea
              className="ct-sms-textarea"
              value={smsMsg}
              onChange={e => setSmsMsg(e.target.value.slice(0, 160))}
              placeholder="Écris ton message SMS…"
              rows={4}
              autoFocus
            />
            <div className="ct-sms-footer">
              <span className="ct-sms-count" style={{ color: smsInfo(smsMsg).segments > 1 ? '#DC2626' : '#9CA3AF' }}>
                {smsInfo(smsMsg).chars} car. · {smsInfo(smsMsg).segments} SMS{smsInfo(smsMsg).unicode ? ' · emoji ⚠️' : ''}
              </span>
              <button
                className="ct-sms-send"
                disabled={smsSending || !smsMsg.trim()}
                onClick={async () => {
                  setSmsSending(true);
                  try {
                    await api.sendSms({ phone: smsTarget.phone, message: smsMsg.trim(), contact_id: smsTarget.id, type: 'manual' });
                    addNotification(`✅ SMS envoyé à ${smsTarget.first_name}`);
                    setSmsTarget(null);
                  } catch {
                    addNotification('❌ Erreur lors de l\'envoi SMS', 'error');
                  } finally {
                    setSmsSending(false);
                  }
                }}
              >
                {smsSending ? '⏳ Envoi…' : '📤 Envoyer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showDedup && (
        <DedupModal contacts={contacts} onClose={() => setShowDedup(false)} onApply={handleDedupApply} />
      )}
    </div>
  );
}
